<script setup lang="ts">
  import { computed, ref, onMounted, defineAsyncComponent } from 'vue'
  import { useI18n } from 'vue-i18n'
  import FlowCardQC from '@/shared/components/FlowCardQC/FlowCardQC.vue'
  import { useUserStore, useCommonStore } from '@/shared/store'
  import {
    PROCEED_EVENT_NAME,
    LOAD_EVENT_NAME,
    ADULTS_COUNT_MIN,
    MAX_TRAVELLERS_COUNT,
    CHILDREN_COUNT_MIN,
  } from './FlowTravelersWidgetQC.constants'
  import { TravelDirection } from '@/shared/types/common.types'
  import { AnalyticsPageName } from '@/shared/constants/analytics.constants'
  import { usePerformanceTiming, useGoogleAnalytics } from '@/shared/composables'
  import { useRoute } from 'vue-router'
  import WrapperQC from '@qicgroupdoha/qic-connect-ds/components/WrapperQC'
  import LinkQC from '@qicgroupdoha/qic-connect-ds/components/LinkQC'
  import CellQC from '@qicgroupdoha/qic-connect-ds/components/CellQC'
  import { AmountCounterQC } from '@/entities/Flow/components/AmountCounterQC'
  import type { AvailableFlowRoutes } from '@/shared/types/flow.types'

  interface Emits {
    (e: typeof PROCEED_EVENT_NAME): void
    (
      e: typeof LOAD_EVENT_NAME,
      request: () => Promise<any>,
      requestId?: string,
      withLoader?: boolean
    ): void
    (e: 'back'): void
  }

  interface Props {
    typeOfTravel?: Exclude<TravelDirection, TravelDirection.UNKNOWN>
    withBackButton?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    typeOfTravel: TravelDirection.OUTBOUND,
    withBackButton: false,
  })
  const emit = defineEmits<Emits>()

  const NeedHelpModalQC = defineAsyncComponent(
    () =>
      import('@/widgets/Flow/FlowNeedHelpWidgetQC/components/NeedHelpModalQC/NeedHelpModalQC.vue')
  )

  const route = useRoute()
  const gaService = useGoogleAnalytics()
  const userStore = useUserStore()
  const commonStore = useCommonStore()
  const { t } = useI18n()
  const { onPageReady, onPerformanceCheckEnd } = usePerformanceTiming()
  const isInbound = props.typeOfTravel === TravelDirection.INBOUND
  const isLoading = ref(false)

  const annotationText = isInbound
    ? t('inbound.traveling_persons.description.text_new')
    : t('outbound.traveling_persons.annotation.text_new')

  const annotationLink = isInbound
    ? t('inbound.traveling_persons.description.link')
    : t('outbound.traveling_persons.annotation.link')

  const adultsTitle = isInbound
    ? t('inbound.traveling_persons.adults.label')
    : t('outbound.traveling_persons.adults.label')

  const adultsAnnotation = isInbound
    ? t('inbound.traveling_persons.adults.annotation')
    : t('outbound.traveling_persons.adults.annotation')

  const childrenTitle = isInbound
    ? t('inbound.traveling_persons.children.label')
    : t('outbound.traveling_persons.children.label')

  const childrenAnnotation = isInbound
    ? t('inbound.traveling_persons.children.annotation')
    : t('outbound.traveling_persons.children.annotation')

  const adultsCount = ref(userStore.adultsCount)
  const childrenCount = ref(userStore.childrenCount)
  const isContactUsPopupActive = ref(false)
  const totalTravellersCount = computed(() => adultsCount.value + childrenCount.value)
  const isMaxCounterDisabled = computed(() => totalTravellersCount.value >= MAX_TRAVELLERS_COUNT)
  const maxAdultsValue = computed(() => MAX_TRAVELLERS_COUNT - childrenCount.value)
  const maxChildrenValue = computed(() => MAX_TRAVELLERS_COUNT - adultsCount.value)

  const openContactPopup = () => {
    isContactUsPopupActive.value = true
    gaService.sendContactUsPopup(route.name as AvailableFlowRoutes)
  }

  const onProceed = async () => {
    if (totalTravellersCount.value > MAX_TRAVELLERS_COUNT) {
      return
    }

    emit(
      LOAD_EVENT_NAME,
      async () => {
        isLoading.value = true
        userStore.setTravelersCount(adultsCount.value, childrenCount.value, true)
        await commonStore.sendFormData({
          numAdults: adultsCount.value,
          numChildren: childrenCount.value,
        })

        gaService.sendNumberOfTravelersProceed(
          props.typeOfTravel,
          adultsCount.value,
          childrenCount.value,
          userStore.destination?.map((country) => country?.id || '')
        )

        emit(PROCEED_EVENT_NAME)
        isLoading.value = false
      },
      'sendFormData',
      false
    )
  }

  onMounted(() => {
    onPageReady()
    onPerformanceCheckEnd()
    gaService.sendViewPage(props.typeOfTravel, AnalyticsPageName.TRAVELERS)
  })
</script>

<template>
  <FlowCardQC
    :title="t('inbound_traveling_persons_header')"
    with-button
    :is-loading="isLoading"
    :with-back-button="withBackButton"
    @button-click="onProceed"
    @back="emit('back')"
  >
    <template #header>
      <p class="text-medium">
        {{ annotationText }}&nbsp;<LinkQC
          :underline="false"
          data-test="travelers-widget-contact-button"
          @click="openContactPopup"
        >
          {{ annotationLink }}
        </LinkQC>
      </p>
      <NeedHelpModalQC
        v-if="isContactUsPopupActive"
        :page="route.name as AvailableFlowRoutes"
        data-test="travelers-widget-need-help-modal"
        @close="isContactUsPopupActive = false"
      />
    </template>

    <WrapperQC direction="column" gap="var(--spacing-6)">
      <WrapperQC direction="column" gap="var(--spacing-5)">
        <AmountCounterQC
          v-model="adultsCount"
          :title="adultsTitle"
          :description="adultsAnnotation"
          :min-value="isLoading ? adultsCount : ADULTS_COUNT_MIN"
          :max-value="isLoading ? adultsCount : maxAdultsValue"
          data-test="adults-counter"
        />
        <CellQC with-border padding-block="spacing-0" />
        <AmountCounterQC
          v-model="childrenCount"
          :title="childrenTitle"
          :description="childrenAnnotation"
          :min-value="isLoading ? childrenCount : CHILDREN_COUNT_MIN"
          :max-value="isLoading ? childrenCount : maxChildrenValue"
          data-test="children-counter"
        />
      </WrapperQC>

      <p v-if="isMaxCounterDisabled" class="text-medium text-error">
        {{ $t('inbound.traveling_persons.count_warning_new') }}
      </p>
    </WrapperQC>
  </FlowCardQC>
</template>

<style scoped lang="scss">
  .text-error {
    align-self: center;
  }
</style>
